import { useSyncExternalStore } from 'react';
import { NAV_SECTIONS, type NavItem } from '@/layouts/nav-sections';

/**
 * Personal shortcuts — a per-viewer bookmark list of existing pages, never
 * shared with the rest of the church (no server round trip, just
 * `localStorage`, the same posture `app-shell.tsx`'s own collapse/search
 * preferences already take). Stored as `to` paths rather than copies of the
 * nav item itself, so a later label or icon change is picked up for free and
 * a permission later revoked simply drops the item from every view that
 * resolves it through `can()`.
 */

const STORAGE_KEY = 'tabita.shortcuts';

const NAV_ITEM_INDEX = new Map<string, NavItem>(
  NAV_SECTIONS.flatMap((section) => section.items).map((item) => [item.to, item]),
);

/** Every navigable page, in the same grouping the sidebar uses — the source
 *  list the "add a shortcut" browser filters and stars from. */
export { NAV_SECTIONS as SHORTCUT_SOURCE_SECTIONS };

export function resolveNavItem(to: string): NavItem | undefined {
  return NAV_ITEM_INDEX.get(to);
}

function load(): readonly string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();
let cache: readonly string[] = load();

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): readonly string[] {
  return cache;
}

function persist(next: readonly string[]): void {
  cache = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A private window or full storage just means the list doesn't survive
    // this visit — not a broken page.
  }
  for (const listener of listeners) listener();
}

/** Test-only: re-syncs the in-memory cache with whatever `localStorage`
 *  currently holds. Production code never needs this — nothing else clears
 *  storage out from under a running app — but `beforeEach`'s `localStorage.
 *  clear()` does exactly that between tests in the same file, and this
 *  module's cache, once written, otherwise outlives it. */
export function __resetShortcutsForTests(): void {
  persist(load());
}

/**
 * Reactive across every component using it at once — the sidebar, the
 * dashboard widget and the manage page can all be mounted together, and
 * starring an item in one must show up in the others immediately.
 * `useSyncExternalStore` is exactly the built-in for that: one shared
 * `localStorage`-backed value, read consistently by every subscriber.
 */
export function useShortcuts(): {
  readonly shortcuts: readonly string[];
  readonly isShortcut: (to: string) => boolean;
  readonly toggle: (to: string) => void;
} {
  const shortcuts = useSyncExternalStore(subscribe, getSnapshot);

  return {
    shortcuts,
    isShortcut: (to: string) => shortcuts.includes(to),
    toggle: (to: string) => {
      persist(shortcuts.includes(to) ? shortcuts.filter((s) => s !== to) : [...shortcuts, to]);
    },
  };
}
